import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { surveysService } from "../services/surveys";
import { useAuthStore } from "../store/authStore";
import { useState } from "react";

export default function SurveyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [message, setMessage] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["survey", id],
    queryFn: () => surveysService.get(id!),
    enabled: !!id,
  });

  const fundMutation = useMutation({
    mutationFn: surveysService.fund,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["survey", id] });
      queryClient.invalidateQueries({ queryKey: ["wallet"] });
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
      setMessage("Survey funded successfully!");
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(err.response?.data?.error || "Failed to fund survey");
    },
  });

  const activateMutation = useMutation({
    mutationFn: surveysService.activate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["survey", id] });
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
      setMessage("Survey activated successfully!");
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(err.response?.data?.error || "Failed to activate survey");
    },
  });

  const cancelMutation = useMutation({
    mutationFn: surveysService.cancel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["survey", id] });
      queryClient.invalidateQueries({ queryKey: ["wallet"] });
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
      setMessage("Survey cancelled and refunded!");
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(err.response?.data?.error || "Failed to cancel survey");
    },
  });

  const exportMutation = useMutation({
    mutationFn: surveysService.export,
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `survey-${id}-export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setMessage("Survey exported successfully!");
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(err.response?.data?.error || "Failed to export survey");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: surveysService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
      setMessage("Survey deleted successfully!");
      setTimeout(() => {
        navigate("/surveys");
      }, 1000);
    },
    onError: (err: any) => {
      setMessage(err.response?.data?.error || "Failed to delete survey");
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-400">Loading survey...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <p className="text-red-400">Error loading survey: {(error as Error).message}</p>
      </div>
    );
  }

  const survey = data?.survey;

  if (!survey) {
    return (
      <div className="card">
        <p className="text-gray-400">Survey not found.</p>
      </div>
    );
  }

  const isOwner = survey.is_owner;
  const canRespond = survey.status === "active" || survey.status === "collecting";

  const isSuccess = message.includes("success") || message.includes("funded") || message.includes("activated") || message.includes("exported");

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-100 mb-2">{survey.title}</h1>
          {survey.description && (
            <p className="text-gray-400">{survey.description}</p>
          )}
        </div>
        <Link to="/surveys" className="btn btn-secondary">
          Back to Surveys
        </Link>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg border ${
          isSuccess
            ? "bg-green-500/10 border-green-500/20 text-green-400"
            : "bg-red-500/10 border-red-500/20 text-red-400"
        }`}>
          {message}
        </div>
      )}

      {/* Action Buttons */}
      <div className="card mb-8">
        {isOwner ? (
          <div className="flex flex-wrap gap-3">
            {survey.status === "draft" && (
              <>
                <Link
                  to={`/surveys/${id}/edit`}
                  className="btn btn-secondary"
                >
                  Edit Survey
                </Link>
                <button
                  onClick={() => {
                    if (window.confirm("Are you sure you want to delete this survey? This action cannot be undone.")) {
                      deleteMutation.mutate(id!);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 rounded-lg font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                >
                  {deleteMutation.isPending ? "Deleting..." : "Delete Survey"}
                </button>
                <button
                  onClick={() => fundMutation.mutate(id!)}
                  disabled={fundMutation.isPending}
                  className="btn btn-primary"
                >
                  {fundMutation.isPending ? "Funding..." : "Fund Survey"}
                </button>
                <Link
                  to={`/surveys/${id}/analytics`}
                  className="btn btn-secondary"
                >
                  View Analytics
                </Link>
              </>
            )}
            {survey.status === "funded" && (
              <>
                <button
                  onClick={() => activateMutation.mutate(id!)}
                  disabled={activateMutation.isPending}
                  className="btn btn-primary"
                >
                  {activateMutation.isPending ? "Activating..." : "Activate Survey"}
                </button>
                <button
                  onClick={() => cancelMutation.mutate(id!)}
                  disabled={cancelMutation.isPending}
                  className="px-4 py-2 rounded-lg font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                >
                  {cancelMutation.isPending ? "Cancelling..." : "Cancel & Refund"}
                </button>
              </>
            )}
            {survey.status === "active" && (
              <>
                <button
                  onClick={() => cancelMutation.mutate(id!)}
                  disabled={cancelMutation.isPending}
                  className="px-4 py-2 rounded-lg font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                >
                  {cancelMutation.isPending ? "Cancelling..." : "Cancel & Refund"}
                </button>
                <Link
                  to={`/surveys/${id}/analytics`}
                  className="btn btn-secondary"
                >
                  View Analytics
                </Link>
                <button
                  onClick={() => exportMutation.mutate(id!)}
                  disabled={exportMutation.isPending}
                  className="btn btn-secondary"
                >
                  {exportMutation.isPending ? "Exporting..." : "Export CSV"}
                </button>
              </>
            )}
            {survey.status === "completed" && (
              <>
                <Link
                  to={`/surveys/${id}/analytics`}
                  className="btn btn-primary"
                >
                  View Analytics
                </Link>
                <button
                  onClick={() => exportMutation.mutate(id!)}
                  disabled={exportMutation.isPending}
                  className="btn btn-secondary"
                >
                  {exportMutation.isPending ? "Exporting..." : "Export CSV"}
                </button>
              </>
            )}
          </div>
        ) : (
          canRespond && (
            <Link to={`/surveys/${id}/respond`} className="btn btn-primary">
              Respond to Survey
            </Link>
          )
        )}
      </div>

      {/* Survey Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Status</h3>
          <p className="text-xl font-bold capitalize text-gray-100">{survey.status}</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Budget</h3>
          <p className="text-xl font-bold text-primary-500">{survey.budget} coins</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Target Responses</h3>
          <p className="text-xl font-bold text-gray-200">{survey.target_responses}</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Actual Responses</h3>
          <p className="text-xl font-bold text-green-400">{survey.actual_responses}</p>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-100">Questions</h2>
        {survey.questions.map((question, qIndex) => (
          <div key={question.id} className="card">
            <h3 className="text-lg font-semibold text-gray-100 mb-4">
              {qIndex + 1}. {question.question_text}
            </h3>
            <div className="space-y-2">
              {question.options.map((option) => (
                <div
                  key={option.id}
                  className="flex items-center justify-between p-3 bg-dark-800 rounded-lg border border-dark-border"
                >
                  <span className="text-gray-200">{option.option_text}</span>
                  <span className="text-sm text-gray-500">
                    {option.response_count} responses
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
